import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Check, Circle, SkipForward, PauseCircle, PlayCircle, XCircle, Send } from "lucide-react";
import { StatusBadge, TypeBadge, SlaChip, OwnerChip, ActionDialog } from "../components/MrfBits";
import { inputCls } from "../hooks/useMasterMaps";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const TERMINAL = ["Rejected", "Cancelled", "Expired"];
const OPEN = ["Pending Approval", "Approved", "In Sourcing", "In Selection", "Offer Stage"];

function TimerCard({ t }) {
  const color = t.state === "Breached" ? "#BB162B" : t.state === "At Risk" ? "#D97706" : t.state === "Done" ? "#1B7F3B" : "#6B7280";
  const pct = t.elapsed_days != null && t.target_days ? Math.min(100, (t.elapsed_days / t.target_days) * 100) : 0;
  return (
    <div data-testid={`timer-${t.code}`} className="border border-gray-200 rounded-sm p-3 bg-white">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-[#0E2240]">{t.code} · {t.label}</span>
        <OwnerChip owner={t.owner} />
      </div>
      <div className="mt-2 flex items-baseline gap-1">
        <span className="font-serif text-xl tabular-nums" style={{ color }}>{t.elapsed_days != null ? t.elapsed_days : "—"}</span>
        <span className="text-xs text-[#6B7280]">/ {t.target_days}d</span>
      </div>
      <div className="mt-2 h-1 bg-gray-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
      <div className="mt-1.5 text-[10px] uppercase tracking-wide" style={{ color }}>{t.state}</div>
    </div>
  );
}

function buildSteps(req, approvals) {
  const lastReturn = [...approvals].filter((a) => a.action === "Return for Info").map((a) => a.acted_on).sort().pop() || "";
  const steps = [{ label: "Raised", sub: req.raised_by_name ? `by ${req.raised_by_name}` : "", done: !!req.raised_on, on: req.raised_on }];
  (req.approval_levels || []).forEach((role, i) => {
    const entry = [...approvals].filter((a) => a.level_no === i + 1 && ["Approve", "Auto-Escalated"].includes(a.action) && a.acted_on > lastReturn).pop();
    const stale = !entry && approvals.some((a) => a.level_no === i + 1 && a.action === "Approve" && a.acted_on <= lastReturn);
    const skipped = (req.skipped_approvers || []).find((s) => s.level === i + 1);
    const current = req.current_level === i && req.status === "Pending Approval";
    steps.push({
      label: `L${i + 1} · ${role.replace(/_/g, " ")}`,
      sub: entry
        ? `${entry.approver_name} · ${entry.action === "Auto-Escalated" ? "auto-escalated" : "approved"} (${entry.tat_hours}h)`
        : stale ? "Superseded — re-approval required"
          : skipped ? `Escalated past ${skipped.name}`
            : current ? `With ${req.current_owner_name}${req.delegate_of_name ? ` (delegate for ${req.delegate_of_name})` : ""}` : "",
      done: !!entry || (!!skipped && !stale), skipped: !!skipped && !entry, stale, current, on: entry?.acted_on,
    });
  });
  steps.push({ label: "Sourcing", sub: "HR", done: !!req.first_shortlist_on, current: ["Approved", "In Sourcing"].includes(req.status), on: req.final_approval_on });
  steps.push({ label: "Selection", sub: "Business", done: !!req.selection_on, current: req.status === "In Selection", on: req.first_shortlist_on });
  steps.push({ label: "Offer", sub: "HR", done: !!req.offer_released_on, current: req.status === "Offer Stage" && !req.offer_released_on, on: req.selection_on });
  steps.push({ label: "Joining", sub: "Candidate", done: req.status === "Filled", current: !!req.offer_released_on && req.status !== "Filled" && !TERMINAL.includes(req.status), on: req.offer_released_on });
  return steps;
}

export default function MrfDetail() {
  const { reqId } = useParams();
  const navigate = useNavigate();
  const { user, can } = useAuth();
  const [data, setData] = useState(null);
  const [dlg, setDlg] = useState(null);
  const [holdDlg, setHoldDlg] = useState(false);
  const [holdDate, setHoldDate] = useState("");
  const [holdReason, setHoldReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/mrf/${reqId}`).then(({ data }) => setData(data)).catch((e) => toast.error(apiError(e)));
  }, [reqId]);
  useEffect(load, [load]);

  if (!data) return <div className="text-sm text-[#6B7280] py-10 text-center">Loading…</div>;
  const { req, approvals } = data;
  const steps = buildSteps(req, approvals);

  const isOwner = req.status === "Pending Approval" && (req.current_owner_id === user.user_id || user.role === "SYSTEM_ADMIN");
  const isHr = can("requisitions", "work");
  const isRaiser = req.raised_by_id === user.user_id;
  const canHold = can("requisitions", "hold");

  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); load(); window.dispatchEvent(new Event("mrf-badge-refresh")); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  const stage = (action, extra = {}, ok) => run(() => api.post(`/mrf/${reqId}/stage`, { action, ...extra }), ok);
  const confirmAction = async (remarks, date) => {
    setBusy(true);
    try {
      await api.post(`/mrf/${reqId}/action`, { action: dlg, remarks, expected_review_on: date || null });
      toast.success(`${req.req_no} — ${dlg}`); setDlg(null); load();
      window.dispatchEvent(new Event("mrf-badge-refresh"));
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const btn = "px-3.5 py-2 text-sm rounded-sm transition-colors duration-200 disabled:opacity-50";

  return (
    <div data-testid="mrf-detail-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-xs text-[#6B7280] hover:text-[#0E2240] transition-colors duration-200 mb-4">
        <ArrowLeft size={14} /> Back
      </button>

      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-6">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="font-serif text-2xl sm:text-3xl text-[#0E2240] tracking-tight tabular-nums" data-testid="mrf-detail-reqno">{req.req_no}</h1>
            <StatusBadge status={req.status} />
            <SlaChip sla={req.sla_status} />
          </div>
          <div className="mt-1.5 text-sm text-[#6B7280]">
            {req.positions_count} × <span className="text-[#0E2240] font-medium">{req.desig_name}</span> ({req.grade}) at {req.branch_name} · {req.dept_name}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <TypeBadge type={req.requisition_type} forced={req.forced_beyond} />
            <span className={`text-[10px] uppercase tracking-wide ${req.priority === "Critical" ? "text-[#BB162B] font-semibold" : "text-[#6B7280]"}`}>{req.priority} priority</span>
            {req.rework_count > 0 && <span className="text-[10px] text-[#D97706]">Returned {req.rework_count}×</span>}
            {req.auto_drafted && <span className="text-[9px] uppercase text-[#D97706] border border-[#D97706]/30 px-1 rounded-sm">Auto-drafted</span>}
          </div>
          {req.replacing_emp_name && (
            <div className="mt-2 text-xs text-[#6B7280]">Replacing <span className="text-[#0E2240] font-medium">{req.replacing_emp_name}</span> ({req.replacing_emp_code})</div>
          )}
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase tracking-wider text-[#6B7280]">Time to fill</div>
          <div className="font-serif text-3xl tabular-nums" style={{ color: req.ttf_days > req.ttf_target ? "#BB162B" : "#0E2240" }}>
            {req.ttf_days != null ? `${req.ttf_days}d` : "—"}
          </div>
          <div className="text-xs text-[#6B7280]">target {req.ttf_target}d</div>
        </div>
      </div>

      {TERMINAL.includes(req.status) && (
        <div data-testid="mrf-terminal-banner" className="mb-6 border border-[#BB162B]/40 bg-[#BB162B]/5 rounded-sm p-4 text-sm text-[#BB162B]">
          This requisition is {req.status}{req.status === "Expired" ? " — unfilled 90 days after approval; revalidation required." : "."}
        </div>
      )}
      {req.current_stage === "Returned for Info" && req.status === "Draft" && (
        <div data-testid="mrf-returned-banner" className="mb-6 border border-[#D97706]/50 bg-[#D97706]/5 rounded-sm p-4 text-sm text-[#D97706]">
          Returned for more information — update and resubmit. Earlier approvals are superseded; the chain restarts at L1.
        </div>
      )}
      {req.status === "On Hold" && (
        <div data-testid="mrf-hold-banner" className="mb-6 border border-gray-400 bg-gray-100 rounded-sm p-4 text-sm text-[#6B7280]">
          On hold — clock paused. Expected review: <span className="tabular-nums text-[#0E2240]">{req.hold_expected_review}</span>
          {req.hold_history?.length > 0 && <> · {req.hold_history[req.hold_history.length - 1].reason}</>}
        </div>
      )}

      {/* Action bar */}
      <div className="flex flex-wrap gap-2 mb-8" data-testid="mrf-action-bar">
        {req.status === "Draft" && can("requisitions", "write") && (
          <button data-testid="detail-submit" disabled={busy} onClick={() => run(() => api.post(`/mrf/${reqId}/submit`), `${req.req_no} submitted for approval`)}
            className={`${btn} bg-[#0E2240] text-white hover:bg-[#1a3358] flex items-center gap-2`}><Send size={14} /> Submit for Approval</button>
        )}
        {isOwner && (
          <>
            <button data-testid="detail-approve" onClick={() => setDlg("Approve")} className={`${btn} bg-[#1B7F3B] text-white hover:bg-[#166832]`}>Approve</button>
            <button data-testid="detail-reject" onClick={() => setDlg("Reject")} className={`${btn} border border-[#BB162B]/60 text-[#BB162B] hover:bg-[#BB162B]/5`}>Reject</button>
            <button data-testid="detail-return" onClick={() => setDlg("Return for Info")} className={`${btn} border border-gray-300 hover:bg-gray-50`}>Return for Info</button>
            <button data-testid="detail-hold-approval" onClick={() => setDlg("Hold")} className={`${btn} border border-gray-300 text-[#6B7280] hover:bg-gray-50`}>Hold</button>
          </>
        )}
        {isHr && req.status === "Approved" && (
          <button data-testid="detail-begin-sourcing" disabled={busy} onClick={() => stage("begin_sourcing", {}, "Sourcing started — T2 clock running")} className={`${btn} bg-[#1E5AA8] text-white hover:bg-[#17497f]`}>Begin Sourcing</button>
        )}
        {isHr && ["Approved", "In Sourcing"].includes(req.status) && (
          <button data-testid="detail-shortlist" disabled={busy} onClick={() => stage("shortlist", {}, "Shortlist shared — business has 48h to offer interview slots")} className={`${btn} bg-[#1E5AA8] text-white hover:bg-[#17497f]`}>Share Shortlist</button>
        )}
        {(isRaiser || isHr) && req.status === "In Selection" && !req.interview_slots_offered_on && (
          <button data-testid="detail-offer-slots" disabled={busy} onClick={() => stage("offer_slots", {}, "Interview slots recorded")} className={`${btn} border border-[#1E5AA8] text-[#1E5AA8] hover:bg-[#1E5AA8]/5`}>Interview Slots Offered</button>
        )}
        {(isRaiser || isHr) && req.status === "In Selection" && (
          <button data-testid="detail-selected" disabled={busy} onClick={() => stage("selected", {}, "Candidate selected — offer TAT (T4) running")} className={`${btn} bg-[#1E5AA8] text-white hover:bg-[#17497f]`}>Candidate Selected</button>
        )}
        {isHr && req.status === "Offer Stage" && !req.offer_released_on && (
          <button data-testid="detail-release-offer" disabled={busy} onClick={() => stage("release_offer", {}, "Offer released — joining clock (T5) with candidate")} className={`${btn} bg-[#1E5AA8] text-white hover:bg-[#17497f]`}>Release Offer</button>
        )}
        {isHr && req.offer_released_on && !TERMINAL.includes(req.status) && req.status !== "Filled" && req.status !== "On Hold" && (
          <button data-testid="detail-joined" disabled={busy} onClick={() => stage("joined", { joined_count: 1 }, "Joining recorded")} className={`${btn} bg-[#1B7F3B] text-white hover:bg-[#166832]`}>
            Mark 1 Joined ({req.positions_filled} of {req.positions_count} filled)
          </button>
        )}
        {canHold && OPEN.includes(req.status) && req.status !== "Pending Approval" && (
          <button data-testid="detail-hold" disabled={busy} onClick={() => setHoldDlg(true)} className={`${btn} border border-gray-300 text-[#6B7280] hover:bg-gray-50 flex items-center gap-1.5`}>
            <PauseCircle size={14} /> Hold
          </button>
        )}
        {canHold && req.status === "On Hold" && (
          <button data-testid="detail-resume" disabled={busy} onClick={() => run(() => api.post(`/mrf/${reqId}/resume`), "Resumed — clock running again")}
            className={`${btn} bg-[#0E2240] text-white hover:bg-[#1a3358] flex items-center gap-1.5`}><PlayCircle size={14} /> Resume</button>
        )}
        {(isRaiser || ["HR_HEAD", "SYSTEM_ADMIN"].includes(user.role)) && !TERMINAL.includes(req.status) && req.status !== "Filled" && (
          <button data-testid="detail-cancel" disabled={busy}
            onClick={() => window.confirm(`Cancel ${req.req_no}? This cannot be undone.`) && run(() => api.post(`/mrf/${reqId}/cancel`), "Requisition cancelled")}
            className={`${btn} border border-gray-300 text-[#6B7280] hover:bg-gray-50 flex items-center gap-1.5`}><XCircle size={14} /> Cancel</button>
        )}
      </div>

      {/* Accountability clock */}
      <h2 className="font-serif text-lg text-[#0E2240] mb-3">Accountability Clock</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-8" data-testid="accountability-clock">
        {(req.timers || []).map((t) => <TimerCard key={t.code} t={t} />)}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <h2 className="font-serif text-lg text-[#0E2240] mb-3">Stage Tracker</h2>
          <div className="bg-white border border-gray-200 rounded-sm p-5" data-testid="stage-tracker">
            {steps.map((s, i) => (
              <div key={i} className="flex items-start gap-3 relative">
                {i < steps.length - 1 && <div className={`absolute left-[11px] top-6 bottom-0 w-px ${s.done ? "bg-[#1B7F3B]/40" : "bg-gray-200"}`} />}
                <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
                  s.skipped ? "bg-[#D97706] text-white" : s.done ? "bg-[#1B7F3B] text-white" : s.current ? "bg-[#0E2240] text-white" : s.stale ? "bg-[#D97706]/15 text-[#D97706]" : "bg-gray-100 text-gray-400"}`}>
                  {s.skipped ? <SkipForward size={12} /> : s.done ? <Check size={13} /> : <Circle size={8} fill="currentColor" />}
                </div>
                <div className="pb-5 min-w-0">
                  <div className={`text-sm ${s.current ? "font-semibold text-[#0E2240]" : s.done ? "text-[#0E2240]" : "text-[#6B7280]"}`}>
                    {s.label}{s.current && <span className="ml-2 text-[10px] uppercase tracking-wide text-[#D97706]">← stuck here now</span>}
                  </div>
                  {s.sub && <div className={`text-xs mt-0.5 ${s.stale ? "text-[#D97706]" : "text-[#6B7280]"}`}>{s.sub}</div>}
                  {s.on && <div className="text-[10px] text-[#6B7280] tabular-nums">{String(s.on).slice(0, 10)}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h2 className="font-serif text-lg text-[#0E2240] mb-3">Approval Trail</h2>
          <div className="bg-white border border-gray-200 rounded-sm overflow-x-auto" data-testid="approval-trail">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-[#F5F6F8]">
                  {["Lvl", "Actor", "Action", "Remarks", "TAT", "Via"].map((h) => (
                    <th key={h} className="px-3 py-2.5 text-left text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {approvals.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-xs text-[#6B7280]">No actions recorded yet</td></tr>
                ) : approvals.map((a) => (
                  <tr key={a.approval_id} className="border-b border-gray-100 last:border-0">
                    <td className="px-3 py-2 tabular-nums text-xs">L{a.level_no}</td>
                    <td className="px-3 py-2 text-xs">
                      <div className="text-[#0E2240]">{a.approver_name}</div>
                      <div className="text-[10px] text-[#6B7280]">{(a.approver_role || "").replace(/_/g, " ")}</div>
                    </td>
                    <td className="px-3 py-2 text-xs whitespace-nowrap">
                      <span className={a.action === "Approve" ? "text-[#1B7F3B] font-medium" : a.action === "Reject" ? "text-[#BB162B] font-medium" : a.action === "Auto-Escalated" ? "text-[#D97706] font-medium" : "text-[#0E2240]"}>
                        {a.action}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-[#6B7280] max-w-[180px]">{a.remarks || "—"}</td>
                    <td className="px-3 py-2 text-xs tabular-nums whitespace-nowrap">{a.tat_hours}h</td>
                    <td className="px-3 py-2 text-[10px] uppercase text-[#6B7280]">{a.via}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 className="font-serif text-lg text-[#0E2240] mt-6 mb-3">Justification</h2>
          <div className="bg-white border border-gray-200 rounded-sm p-5 text-sm text-[#0E2240] leading-relaxed" data-testid="mrf-justification-box">
            {req.justification}
            {req.override_duplicate?.reason && (
              <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-[#D97706]">Duplicate override: {req.override_duplicate.reason}</div>
            )}
          </div>

          {req.hold_history?.length > 0 && (
            <>
              <h2 className="font-serif text-lg text-[#0E2240] mt-6 mb-3">Hold History</h2>
              <div className="bg-white border border-gray-200 rounded-sm p-5 space-y-2" data-testid="hold-history">
                {req.hold_history.map((h, i) => (
                  <div key={i} className="text-xs text-[#6B7280]">
                    <span className="tabular-nums">{String(h.from).slice(0, 10)} → {h.to ? String(h.to).slice(0, 10) : "ongoing"}</span> · {h.by} · {h.reason || "no reason"} <span className="text-[10px]">({h.stage})</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {dlg && (
        <ActionDialog open onOpenChange={(o) => !o && setDlg(null)} action={dlg}
          reqNo={`${req.req_no} — ${req.desig_name} at ${req.branch_name}`} onConfirm={confirmAction} busy={busy} />
      )}

      <Dialog open={holdDlg} onOpenChange={setHoldDlg}>
        <DialogContent className="bg-white rounded-sm shadow-none border border-gray-200 max-w-md">
          <DialogHeader><DialogTitle className="font-serif text-[#0E2240]">Put on hold — clock pauses</DialogTitle></DialogHeader>
          <div className="space-y-3 py-1">
            <div>
              <label className="text-xs text-[#6B7280] block mb-1">Expected review date (mandatory)</label>
              <input data-testid="hold-date" type="date" className={inputCls} value={holdDate} onChange={(e) => setHoldDate(e.target.value)} />
            </div>
            <input data-testid="hold-reason" className={inputCls} placeholder="Reason" value={holdReason} onChange={(e) => setHoldReason(e.target.value)} />
          </div>
          <DialogFooter>
            <button onClick={() => setHoldDlg(false)} className="px-4 py-2 text-sm border border-gray-300 rounded-sm hover:bg-gray-50">Cancel</button>
            <button data-testid="hold-confirm" disabled={!holdDate || busy}
              onClick={() => run(() => api.post(`/mrf/${reqId}/hold`, { expected_review_on: holdDate, reason: holdReason }), "On hold — clock paused").then(() => setHoldDlg(false))}
              className="px-4 py-2 text-sm bg-[#0E2240] text-white rounded-sm hover:bg-[#1a3358] disabled:opacity-50">Confirm Hold</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
